/**
 * Adds a Google Tag Manager interface to the app.
 */
export default {
  /**
   * Install method of the Google Tag Manager plugin.
   *
   * @param {Object} app - The Vue instance
   * @param {Object} [options] - Additional plugin options
   * @param {Boolean} [options.debug = false] - Allows to en-/disable debugging functionality.
   * @param {String} [options.dataLayer = 'dataLayer'] - Allows to define a custom dataLayer name.
   */
  install(app, options = {}) {
    const { dataLayer = 'dataLayer' } = options;
    let { debug } = options;

    /**
     * Push a new event to the dataLayer.
     *
     * @param {Object} payload - The new event data, which will be added to the dataLayer.
     */
    function push(payload) {
      if (window[dataLayer]?.push) {
        window[dataLayer].push(payload);

        // Log if debug and development mode are active
        if (import.meta.env.DEV && debug) {
          console.group('GTM debug'); // eslint-disable-line no-console
          console.log('payload', payload); // eslint-disable-line no-console
          console.log('dataLayer', window[dataLayer]); // eslint-disable-line no-console
          console.groupEnd(); // eslint-disable-line no-console
        }
      } else {
        throw new Error('Google Tag Manager dataLayer is not available!');
      }
    }

    /**
     * Pushes the userAction GTM event.
     * This event can be used for every default action.
     *
     * @param {Object} payload - The payload object.
     * @param {String} payload.category - The eventCategory variable.
     * @param {String} payload.action - The action variable.
     * @param {String} payload.label - The eventLabel variable.
     */
    function pushUserAction({ category, action, label }) {
      push({
        eventCategory: category || '',
        action: action || '',
        eventLabel: label || '',
        event: 'userAction',
      });
    }

    app.config.globalProperties.$gtm = {
      push,

      /**
       * Tracks, when the user tries to book a course, but needs to login first.
       */
      pushCourseLoginRequired() {
        pushUserAction({
          category: 'Academy',
          action: 'Booking',
          label: 'Login required',
        });
      },

      /**
       * Tracks when the booking form for a course is shown.
       */
      pushCourseBookingForm() {
        pushUserAction({
          category: 'Academy',
          action: 'Booking',
          label: 'Booking form shown',
        });
      },

      /**
       * Tracks the final booking for a course.
       */
      pushCourseBooking() {
        pushUserAction({
          category: 'Academy',
          action: 'Booking',
          label: 'Booking completed',
        });
      },

      /**
       * Tracks successful/failed quiz attempts.
       *
       * @param {Boolean} success - A flag to set the result of the quiz.
       */
      pushCourseQuiz(success) {
        pushUserAction({
          category: 'Academy',
          action: 'Quiz',
          label: success ? 'Quiz passed' : 'Quiz failed',
        });
      },

      /**
       * Enable/disable debugging.
       *
       * @param {Boolean} [enable = true] Flag if debugging should be en- or disabled.
       */
      debug(enable) {
        debug = enable !== false;
      },
    };
  },
};
